import type { RcFile } from 'antd/lib/upload';
import { parse } from 'csv-parse/sync';
import { stringify } from 'csv-stringify/sync';
import { format } from 'date-fns/format';

export const convertCsvToMatrix = (csvString: string): string[][] => {
  return parse(csvString, {
    bom: true,
    columns: false,
    trim: true,
    relax_column_count: true,
  });
};

// Forces a spreadsheet (Excel, Numbers, Sheets) to treat a CSV cell as text rather than
// a number, so leading zeros in values like postal code "03049" survive being opened and
// re-saved. The value is wrapped as an `="…"` formula, which all major spreadsheet apps
// render as the literal text. (A leading tab is not enough — Numbers still numeric-coerces
// it.) On re-upload, stripCsvTextForcing reverses the wrapper, keeping the
// export -> edit -> re-upload round-trip lossless.
export const forceCsvCellAsText = (value: string): string => `="${value}"`;

// Reverses forceCsvCellAsText on a value read back from an uploaded CSV. Bare values
// (a hand-authored file, or one never opened in a spreadsheet) are returned untouched.
export const stripCsvTextForcing = (value: string): string =>
  value.startsWith('="') && value.endsWith('"') ? value.slice(2, -1) : value;
// snake_case => camelCase
export const snakeToCamel = (str: string) => {
  const sections = str.split('_');
  let camelString = sections[0];
  for (let i = 1; i < sections.length; i++) {
    camelString += sections[i][0].toUpperCase() + sections[i].slice(1);
  }
  return camelString;
};

// kebab-case => kebabCase
export function kebabToCamel(str: string) {
  return str ? str.replace(/-./g, (x) => x[1].toUpperCase()) : str;
}

// camelCase => kebab-case
// find each uppercase letter, add the dash and lowercase it
export function camelToKebab(str: string) {
  return str
    ? [...str].reduce((prev, s) => {
        if (/[A-Z]/.test(s)) {
          prev = prev.concat('-');
          s = s.toLowerCase();
        }

        return prev.concat(s);
      }, '')
    : str;
}

export const csvMatrixToObjectList = (headers: string[], matrix: string[][]) => {
  return matrix.map((row) => {
    const obj: Record<string, string> = {};
    for (let idx = 0; idx < row.length; idx++) {
      obj[headers[idx]] = row[idx];
    }
    return obj;
  });
};

export const convertMatrixToCsvString = async (matrix: any[][]): Promise<string> => {
  return stringify(matrix).trim();
};

export const rowObjectListToMatrix = (rowList: Record<any, any>[]): any[][] => {
  if (!rowList?.length) {
    return [];
  }
  const headerRow = Object.keys(rowList[0]);
  const csvMatrix = rowList.map((val) => Object.values(val));
  csvMatrix.unshift(headerRow);
  return csvMatrix;
};
export const downloadTextFile = (str: string, name: string): void => {
  const txtContent = 'data:text/plain;charset=utf-8,' + str;
  const encodedUri = encodeURI(txtContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${name}.txt`);
  document.body.appendChild(link);
  link.click();
};
export const downloadCsvFile = (csvString: string, name: string): void => {
  const csvData = new Blob([csvString], { type: 'text/csv' });
  const csvUrl = URL.createObjectURL(csvData);
  const link = document.createElement('a');
  link.setAttribute('href', csvUrl);
  link.setAttribute('download', `${name}.csv`);
  document.body.appendChild(link);
  link.click();
};
export const downloadYamlFile = (yamlString: string, name: string): void => {
  const yamlContent = 'data:text/yaml;charset=utf-8,' + yamlString;
  const encodedUri = encodeURI(yamlContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${name}.yaml`);
  document.body.appendChild(link);
  link.click();
};

export const downloadZplFile = (zpl: string, name: string): void => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(new Blob([zpl], { type: 'text/plain' }));
  link.setAttribute('download', `${name}-${format(Date.now(), 'yyyy-MM-dd')}.zpl`);
  document.body.appendChild(link);
  link.click();
};

// promisifies reading a file to text from antd
export const readFileAsString = async (fileData: File): Promise<string> => {
  const FR = new FileReader();
  FR.readAsText(fileData);
  return await new Promise((resolve, reject) => {
    try {
      FR.onloadend = (evt: any) => {
        const csvParsed = evt.target.result;
        resolve(csvParsed);
      };
    } catch (err) {
      return reject(err);
    }
  });
};
export const readFirstNRowsOfCSVFile = async (file: File, rowCount = 2, chunkSize = 5000) => {
  let i = 0;
  let csvString = '';
  while (csvString.split('\n').length <= rowCount) {
    const start = i * chunkSize;
    if (start >= file.size) {
      break;
    }
    const chunk = file.slice(start, Math.min(start + chunkSize, file.size));
    const chunkString = await chunk.text();
    csvString += chunkString;
    i += 1;
  }
  return csvString.split('\n').slice(0, rowCount).join('\n');
};

const validateRequiredHeaders = (row: string[], headers: string[]) => {
  const usedHeaders = new Set(row);
  const missingHeaders: string[] = [];
  headers.forEach((h: string) => {
    if (!usedHeaders.has(h)) {
      missingHeaders.push(h);
    }
  });
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
  }
};
const validateProvidedHeaders = (row: string[], headers: string[]) => {
  const allowedHeaders = new Set(headers);
  const unknownHeaders: string[] = [];
  row.forEach((h) => {
    if (!allowedHeaders.has(h)) {
      unknownHeaders.push(h);
    }
  });
  if (unknownHeaders.length) {
    throw new Error(`Recieved following unknown headers: ${unknownHeaders.join(', ')}`);
  }
};
// Number of rows read when checking for data rows. More than 2 so that a stray blank line between
// the header and the first record (some spreadsheet exports emit one) is not mistaken for an empty
// file; bounded so the check stays a small chunked read rather than loading the whole upload.
const DATA_ROW_SCAN_ROWS = 5;

/**
 * Rejects a CSV that has a header row but no data rows. Downstream, such a file uploads and
 * "succeeds" while activating nothing, so catching it here is the only point where the person who
 * built the file can still act on it.
 */
export const validateCsvHasDataRows = async (file: RcFile) => {
  const csvString = await readFirstNRowsOfCSVFile(file, DATA_ROW_SCAN_ROWS);
  const matrix = convertCsvToMatrix(csvString);

  const hasDataRow = matrix.slice(1).some((row) => row.some((cell) => cell.trim() !== ''));
  if (!hasDataRow) {
    throw new Error(
      'This file has a header row but no data rows. Add at least one row of transit times before uploading.',
    );
  }
};

export const validateCsvHeaders =
  (requiredHeaders: string[], providedHeaders: string[], allowUnknown?: boolean) => async (file: RcFile) => {
    const csvString = await readFirstNRowsOfCSVFile(file, 1);
    const matrix = convertCsvToMatrix(csvString);

    const headerRow = matrix[0];
    validateRequiredHeaders(headerRow, requiredHeaders);

    if (allowUnknown) {
      return;
    }

    validateProvidedHeaders(headerRow, providedHeaders);
  };
